import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import * as React from "react";
import { useGetOnboardingSession } from "@/api/onboarding-session";
import { useResyncOnboardingSession } from "@/api/onboarding-session";
import {
  FundingDialogAction,
  SecondaryInviteDialogAction,
  ChangeSecondarySignerDialogAction,
  RemoveSecondaryDialogAction,
} from "@/components/actions/session-actions";
import { formatDate } from "@/lib/date";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetUserDocuments } from "@/api/user-documents";
import { ArrowLeft, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { OnboardingSession } from "@/api/types";
import { Checkbox } from "@/components/ui/checkbox";

export const Route = createFileRoute("/_auth/sessions/$sessionId")({
  component: SessionDetailPage,
});

function typeLabel(onboardingType?: string) {
  return onboardingType === "clientInfo"
    ? "Primary Signatory"
    : "Secondary Signatory";
}

async function copyToClipboard(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Could not copy");
  }
}

function SessionDetailPage() {
  const { sessionId } = useParams({ from: "/_auth/sessions/$sessionId" });
  const { data, isLoading, isError } = useGetOnboardingSession(sessionId);
  const resync = useResyncOnboardingSession();

  const session = data?.session;
  const linkedId = React.useMemo(() => {
    if (session?.parentSessionId) {
      return session.parentSessionId;
    }
    return data?.linkedSessions.find(
      (s: OnboardingSession) => s.parentSessionId === sessionId
    )?.sessionId;
  }, [data, session, sessionId]);
  const linkedSession = React.useMemo(
    () =>
      data?.linkedSessions.find(
        (s: OnboardingSession) => s.sessionId === linkedId
      ),
    [data, linkedId]
  );
  if (isLoading) {
    return (
      <section className="grid gap-4 p-2">
        <Skeleton className="h-6 w-60" />
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-48 w-full" />
      </section>
    );
  }
  if (isError) {
    return <p className="p-2 text-red-600">Failed to load session.</p>;
  }

  if (!session) {
    return <p className="p-2">Session not found.</p>;
  }

  const attachments = Object.entries(session.data ?? {}).filter(
    ([, value]) => typeof value === "string" && value.startsWith("FILE#")
  ) as [string, string][];
  const accountType = String(
    (session.data as Record<string, unknown>)?.accountTypeDropdown ?? ""
  );
  const isIndividualsPrimary =
    session.onboardingType === "clientInfo" &&
    accountType.startsWith("Individuals");

  return (
    <section className="grid min-w-0 max-w-full gap-6 p-2">
      <div className="flex items-center">
        <Button asChild variant="ghost" size="sm">
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Link>
        </Button>
      </div>
      <header className="grid gap-2">
        <TooltipProvider>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-0 max-w-full items-center gap-1">
            <h1 className="min-w-0 truncate text-xl font-semibold">
              {session.email}
            </h1>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label="Copy email"
                  onClick={() => void copyToClipboard(session.email, "Email")}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy email</TooltipContent>
            </Tooltip>
          </div>
          <Badge variant="secondary">{typeLabel(session.onboardingType)}</Badge>
          <Badge className="capitalize">{session.status}</Badge>
          <div className="ml-auto flex items-center gap-2">
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      resync.mutate({
                        cognitoId: session.cognitoId,
                        sessionId: session.sessionId,
                      })
                    }
                    disabled={
                      resync.isPending || session.status !== "complete"
                    }
                  >
                    {resync.isPending
                      ? "Resyncing..."
                      : "Resync to Salesforce"}
                  </Button>
                </span>
              </TooltipTrigger>
              {session.status !== "complete" ? (
                <TooltipContent>
                  Resync allowed only when status is complete
                </TooltipContent>
              ) : null}
            </Tooltip>
            <FundingDialogAction
              session={session}
              linkedSession={linkedSession}
              disabled={session.status !== "complete"}
              trigger={
                <Button size="sm" variant="secondary">
                  Resend funding instructions
                </Button>
              }
            />
            {session.onboardingType !== "clientInfo" ? (
              <SecondaryInviteDialogAction
                session={session}
                disabled={session.cognitoId !== "RESERVED"}
                trigger={
                  <Button size="sm" variant="secondary">
                    Resend secondary signup email
                  </Button>
                }
              />
            ) : null}
            {session.onboardingType === "clientInfo" && linkedSession ? (
              <ChangeSecondarySignerDialogAction
                session={session}
                linkedSession={linkedSession}
                trigger={
                  <Button size="sm" variant="secondary">
                    Change secondary signer
                  </Button>
                }
              />
            ) : null}
            {isIndividualsPrimary ? (
              <RemoveSecondaryDialogAction
                session={session}
                linkedSession={linkedSession}
                trigger={
                  <Button size="sm" variant="destructive">
                    Remove secondary signer
                  </Button>
                }
              />
            ) : null}
            {linkedId && (
              <Button asChild size="sm" variant="ghost">
                <Link
                  to="/sessions/$sessionId"
                  params={{ sessionId: linkedId }}
                >
                  {session.onboardingType === "clientInfo"
                    ? "Go to Secondary"
                    : "Go to Primary"}
                </Link>
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1 font-mono">
            Session ID: {session.sessionId}
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  aria-label="Copy session ID"
                  onClick={() =>
                    void copyToClipboard(session.sessionId, "Session ID")
                  }
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy session ID</TooltipContent>
            </Tooltip>
          </span>
          <span className="inline-flex items-center gap-1 font-mono">
            Cognito ID: {session.cognitoId}
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  aria-label="Copy Cognito ID"
                  onClick={() =>
                    void copyToClipboard(session.cognitoId, "Cognito ID")
                  }
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy Cognito ID</TooltipContent>
            </Tooltip>
          </span>
          <span className="ml-auto">
            Created {formatDate(session.createdAt)} · Updated{" "}
            {formatDate(session.updatedAt)}
          </span>
        </div>
        </TooltipProvider>
      </header>

      {session.salesforceSyncStatus === "sync_failed" &&
        session.salesforceSyncErrorMessage && (
          <div className="rounded-md border border-red-500 bg-red-50 p-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-900 mb-1">
                  Salesforce Sync Failed
                </h3>
                <p className="text-sm text-red-800 whitespace-pre-wrap">
                  {session.salesforceSyncErrorMessage}
                </p>
              </div>
            </div>
          </div>
        )}

      <div className="grid gap-2">
        <h2 className="font-medium">Data</h2>
        <div className="rounded-md border p-3 text-sm">
          <pre className="whitespace-pre-wrap break-words">
            {JSON.stringify(session.data ?? {}, null, 2)}
          </pre>
        </div>
      </div>

      <div className="grid gap-3">
        <h2 className="font-medium">Attachments</h2>
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="p-2 text-left">Field</th>
                <th className="p-2 text-left">Value</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {attachments.length > 0 ? (
                attachments.map(([field, value]) => (
                  <AttachmentRow
                    key={field}
                    sessionId={session.sessionId}
                    field={field}
                    documentId={value}
                  />
                ))
              ) : (
                <tr>
                  <td className="p-2" colSpan={3}>
                    No attachments
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-3">
        <h2 className="font-medium">Documents</h2>
        <div className="rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(session.documents) &&
              session.documents.length > 0 ? (
                session.documents.map((doc: any) => (
                  <DocumentRow
                    key={doc.documentId}
                    sessionId={session.sessionId}
                    doc={doc}
                  />
                ))
              ) : (
                <tr>
                  <td className="p-2" colSpan={3}>
                    No documents
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

// consolidated action components moved to '@/components/actions/session-actions'

const DOCUMENSO_BASE = import.meta.env.VITE_DOCUMENSO_BASE as string;
function DocumentRow({
  sessionId,
  doc,
}: {
  sessionId: string;
  doc: { documentId: string; name: string; status: string };
}) {
  const openDoc = () => {
    const url = `${DOCUMENSO_BASE}/documents/${encodeURIComponent(doc.documentId)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <tr>
      <td className="p-2">{doc.name}</td>
      <td className="p-2">
        <Badge>{doc.status}</Badge>
      </td>
      <td className="p-2">
        <Button size="sm" variant="secondary" onClick={openDoc}>
          Open in Documenso
        </Button>
      </td>
    </tr>
  );
}

function AttachmentRow({
  sessionId,
  field,
  documentId,
}: {
  sessionId: string;
  field: string;
  documentId: string;
}) {
  const { refetch, isFetching } = useGetUserDocuments(
    {
      sessionId,
      documentId: encodeURIComponent(documentId),
    },
    { enabled: false }
  );

  const openAttachment = async () => {
    const { data } = await refetch();
    if (data) {
      window.open(String(data), "_blank", "noopener,noreferrer");
    }
  };

  return (
    <tr>
      <td className="p-2">{field}</td>
      <td className="p-2">{documentId}</td>
      <td className="p-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={openAttachment}
          disabled={isFetching}
        >
          {isFetching ? "Loading..." : "Open"}
        </Button>
      </td>
    </tr>
  );
}
